import History from 'history';
import { runInAction } from 'mobx';
import { HistoryService } from 'services/base/history';
import { FCMMessage, FCMMessageKind } from 'services/base/ipc_types';
import { UnreachableFunction } from 'ui/base/types';
import { BasePathItems, BasePath } from 'services/base/page_path';
import { GlobalAccountsDomain } from 'services/accounts/accounts_domain';
import { GlobalPangolinDomains } from 'services/pangolin/pangolin_domain';

export function redirectByNotification(history: History.History, notification: FCMMessage): Promise<boolean> {
  return runInAction(async() => {
    HistoryService.clear(history);

    await HistoryService.replace(history, {
      kind: BasePath.HOME,
      pangolinID: notification.pangolinID,
    });

    if (!checkNotificationOK(notification)) {
      return false;
    }

    switch(notification.kind) {
      case FCMMessageKind.HOST_CONNECTED:
        await HistoryService.push(history, {
          kind: BasePath.DEVICES_LIST,
          pangolinID: notification.pangolinID,
        });
        await HistoryService.push(history, {
          kind: BasePath.DEVICES_VIEW,
          pangolinID: notification.pangolinID,
          deviceID: notification.hostID,
        });
        return true;
      case FCMMessageKind.NEW_INCIDENT:
        await HistoryService.push(history, {
          kind: BasePath.LIST_ISSUES,
          pangolinID: notification.pangolinID,
        });
        await HistoryService.push(history, {
          kind: BasePath.LIST_ISSUES,
          pangolinID: notification.pangolinID,
          itemsPath: {
            kind: BasePathItems.VIEW_ITEM,
            itemID: notification.issueID,
          },
        });
        return true;
      case FCMMessageKind.ONLINE_STATUS_UPDATE:
      case FCMMessageKind.UPGRADE_STATUS_UPDATE:
      case FCMMessageKind.RESTORE_STATUS_UPDATE:
        // not supposed to be handled
        return false;
      default:
        return UnreachableFunction(notification);
    }
  });
}

function checkNotificationOK(notification: FCMMessage): boolean {
  switch(notification.kind) {
    case FCMMessageKind.HOST_CONNECTED:
      if (!checkPangolinExists(notification.pangolinID)) {
        return false;
      }
      return checkHostExists(notification.pangolinID, notification.hostID);
    case FCMMessageKind.NEW_INCIDENT:
      if (!checkPangolinExists(notification.pangolinID)) {
        return false;
      }
      return checkIssueIncidentLoaded(
        notification.pangolinID,
        notification.issueID,
        notification.incidentID,
      );
    case FCMMessageKind.ONLINE_STATUS_UPDATE:
    case FCMMessageKind.UPGRADE_STATUS_UPDATE:
    case FCMMessageKind.RESTORE_STATUS_UPDATE:
      // not supposed to be handled
      return false;
    default:
      return UnreachableFunction(notification);
  }
}

function checkPangolinExists(pangolinID: string): boolean {
  const pangolin = GlobalAccountsDomain.pangolins.get(pangolinID);
  return pangolin != null;
}

function checkHostExists(pangolinID: string, hostID: string): boolean {
  const pangolin = GlobalPangolinDomains.domains.get(pangolinID);
  return pangolin?.hosts.get(hostID) != null;
}

function checkIssueIncidentLoaded(pangolinID: string, issueID: string, incidentID: string): boolean {
  const pangolin = GlobalPangolinDomains.domains.get(pangolinID);
  return (pangolin?.issuesItem?.fpid === issueID &&
          pangolin?.issuesItem?.incidents.has(incidentID));
}
